import { PlanIntervals } from 'src/constants/planData'

// Helper function to create network logo objects
export const createNetworkLogo = (network, width = 40, height = 40) => ({
  logo: network.logoWhite || network.logoUrl,
  alt: network.alt,
  width,
  height,
})

export const formatListText = (items = [], lang = 'en') => {
  if (!items?.length) return ''

  if (items.length === 1) {
    return items[0]
  } else if (items.length === 2) {
    const connector = lang === 'en' ? 'and' : 'y'
    return `${items[0]} ${connector} ${items[1]}`
  } else {
    const allButLast = items.slice(0, -1).join(', ')
    const last = items[items.length - 1]
    const connector = lang === 'en' ? ', and' : ', y'
    return `${allButLast}${connector} ${last}`
  }
}

// Calculate the savings note for a plan
export const calculateSavingsNote = (market, price, discountedPrice) => {
  if (!discountedPrice) return ''
  const savings = price - discountedPrice
  return savings > 0 ? `$${savings.toFixed(2)} off` : null
}

export const getMonthlyPrice = (price, interval) => {
  if (!price) return null
  let monthlyPrice = price
  if (interval === PlanIntervals.monthly) monthlyPrice = price
  if (interval === PlanIntervals.quarterly) monthlyPrice = price / 3
  if (interval === PlanIntervals.annual) monthlyPrice = price / 12
  monthlyPrice = Math.round(monthlyPrice * 1e2) / 1e2
  return monthlyPrice.toFixed(2)
}

export const createFootnote = (data, interval, currency = '') => {
  const totalPrice = data.price?.toFixed(2)
  const discountedTotalPrice = data.discountedPrice?.toFixed(2)
  // Get number of months for the interval
  const monthMap = {
    [PlanIntervals.annual]: 12,
    [PlanIntervals.quarterly]: 3,
    [PlanIntervals.monthly]: 1,
  }
  const numOfMonths = monthMap[interval]

  const monthText = numOfMonths === 1 ? 'month' : 'months'

  if (totalPrice) {
    if (discountedTotalPrice && numOfMonths) {
      return `*Average cost per month. Charged for the first ${numOfMonths} ${monthText} at ${currency}$${discountedTotalPrice}, then ${currency}$${totalPrice} every ${numOfMonths} ${monthText}. Prices subject to change. Additional taxes and fees may apply.`
    } else if (numOfMonths) {
      if (numOfMonths === 1) {
        return `*Average cost per month. Charged every month at ${currency}$${totalPrice}. Prices subject to change. Additional taxes and fees may apply.`
      } else {
        return `*Average cost per month. Charged every ${numOfMonths} ${monthText} at ${currency}$${totalPrice}. Prices subject to change. Additional taxes and fees may apply.`
      }
    } else if (discountedTotalPrice) {
      return `*Renews at full price of ${currency}$${totalPrice}. Prices subject to change. Additional taxes and fees may apply.`
    }
  }
  return ''
}

/**
 * Build a plan with interval-specific overrides
 * @param {Object} plan - Plan data object (e.g., canadaPlanData.premium)
 * @param {string} interval - Billing interval (e.g., PlanIntervals.monthly)
 * @param {Object} overrides - Overrides to apply to the plan (e.g., { monthly: { footnote: 'only available on monthly' } })
 * @returns {Object|null} Built plan or null if no price
 */
export function buildPlan(market, plan, interval, overrides = {}) {
  // Early return for null overrides
  if (overrides?.[interval] === null) return null

  // Get interval data early
  const intervalData = plan?.intervals?.[interval]
  if (!intervalData?.price) return null

  // Combine overrides efficiently
  const intervalOverrides = {
    ...overrides.all,
    ...overrides?.[interval],
  }

  // Build base object with minimal spreading
  const base = Object.assign({}, plan, intervalData)

  // Apply overrides first to get final prices
  const withOverrides = {
    ...base,
    market,
    interval,
    ...intervalOverrides,
  }

  // Calculate savings note with final prices
  const savingsNote =
    withOverrides.savingsNote ||
    (market === 'canada'
      ? calculateSavingsNote(market, withOverrides.price, withOverrides.discountedPrice)
      : null)

  // Create footnote with final prices (unless footnote is overridden)
  const footnote = withOverrides.footnote
    ? typeof withOverrides.footnote === 'function'
      ? withOverrides.footnote()
      : withOverrides.footnote
    : createFootnote(
        { price: withOverrides.price, discountedPrice: withOverrides.discountedPrice },
        interval
      )

  // Return optimized object
  return {
    ...withOverrides,
    savingsNote,
    footnote: () => footnote,
  }
}

export const modifyListItems = (list = [], map = {}) => {
  return list.map((item, idx) => {
    if (!map.hasOwnProperty(idx)) return item

    const replacement = map[idx]
    // If replacement is an object, spread it to replace multiple properties
    // If it's a boolean, just set the available property
    return typeof replacement === 'object'
      ? { ...item, ...replacement }
      : { ...item, available: replacement }
  })
}

/**
 * Calculate the lowest monthly equivalent price from all plans and intervals
 * @param {Array} plans - Array of plan objects with intervals structure
 * @param {Array|null} intervals - Optional array of intervals to filter by [PlanIntervals.monthly, PlanIntervals.quarterly, PlanIntervals.annual]
 * @returns {number|null} The lowest monthly equivalent price, or null if no valid prices found
 * @example
 * // Compare all intervals (default behavior)
 * calculateLowestPrice(plans)
 * // Compare only monthly and annual
 * calculateLowestPrice(plans, [PlanIntervals.monthly, PlanIntervals.annual])
 */
export const calculateLowestPrice = (plans = [], intervals = null) => {
  if (!plans.length) return null
  if (intervals && intervals.length === 0) return null

  const validIntervals = intervals ? new Set(intervals) : null

  // Get all valid monthly prices from all plans and all intervals
  const allPrices = plans
    .flatMap((plan) => {
      // Handle plans without intervals structure
      if (!plan.intervals) {
        const prices = []
        if (plan.price) prices.push(getMonthlyPrice(plan.price, PlanIntervals.monthly))
        if (plan.discountedPrice)
          prices.push(getMonthlyPrice(plan.discountedPrice, PlanIntervals.monthly))
        return prices
      }

      // Handle plans with intervals
      return Object.entries(plan.intervals)
        .filter(([interval]) => !validIntervals || validIntervals.has(interval))
        .flatMap(([interval, intervalData]) => {
          const prices = []
          if (intervalData?.price) prices.push(getMonthlyPrice(intervalData.price, interval))
          if (intervalData?.discountedPrice)
            prices.push(getMonthlyPrice(intervalData.discountedPrice, interval))
          return prices
        })
    })
    .filter((price) => price !== null && price !== undefined)

  return allPrices.length ? parseFloat(Math.min(...allPrices).toFixed(2)) : null
}
